"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.MetricsAggregator = void 0;

var _time = require("../utils-hoist/time.js");

var _spanUtils = require("../utils/spanUtils.js");

var _constants = require("./constants.js");

var _envelope = require("./envelope.js");

var _instance = require("./instance.js");

var _utils = require("./utils.js");

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i]; return arr2; }

function _iterableToArrayLimit(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

/**
 * A metrics aggregator that aggregates metrics in memory and flushes them periodically.
 */
class MetricsAggregator {
  // TODO(@anonrig): Use FinalizationRegistry to have a proper way of flushing the buckets
  // when the aggregator is garbage collected.
  // Ref: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/FinalizationRegistry
  // Different metrics have different weights. We use this to limit the number of metrics
  // that we store in memory.
  // We adjust the type here to add the `unref()` part, as setInterval can technically return a number or a NodeJS.Timer
  // SDKs are required to shift the flush interval by random() * rollup_in_seconds.
  // That shift is determined once per startup to create jittering.
  // An SDK is required to perform force flushing ahead of scheduled time if the memory
  // pressure is too high. There is no rule for this other than that SDKs should be tracking
  // abstract aggregation complexity (eg: a counter only carries a single float, whereas a
  // distribution is a float per emission).
  //
  // Force flush is used on either shutdown, flush() or when we exceed the max weight.
  constructor(_client) {
    this._client = _client;
    this._buckets = new Map();
    this._bucketsTotalWeight = 0;
    this._interval = setInterval(() => this._flush(), _constants.DEFAULT_FLUSH_INTERVAL);

    if (this._interval.unref) {
      this._interval.unref();
    }

    this._flushShift = Math.floor(Math.random() * _constants.DEFAULT_FLUSH_INTERVAL / 1000);
    this._forceFlush = false;
  }
  /**
   * @inheritDoc
   */


  add(metricType, unsanitizedName, value, unsanitizedUnit = 'none', unsanitizedTags = {}, maybeFloatTimestamp = (0, _time.timestampInSeconds)()) {
    const timestamp = Math.floor(maybeFloatTimestamp);
    const name = (0, _utils.sanitizeMetricKey)(unsanitizedName);
    const tags = (0, _utils.sanitizeTags)(unsanitizedTags);
    const unit = (0, _utils.sanitizeUnit)(unsanitizedUnit);
    const bucketKey = (0, _utils.getBucketKey)(metricType, name, unit, tags);

    let bucketItem = this._buckets.get(bucketKey); // If this is a set metric, we need to calculate the delta from the previous weight.


    const previousWeight = bucketItem && metricType === _constants.SET_METRIC_TYPE ? bucketItem.metric.weight : 0;

    if (bucketItem) {
      bucketItem.metric.add(value); // TODO(abhi): Do we need this check?

      if (bucketItem.timestamp < timestamp) {
        bucketItem.timestamp = timestamp;
      }
    } else {
      bucketItem = {
        // @ts-expect-error we don't need to narrow down the type of value here, saves bundle size.
        metric: new _instance.METRIC_MAP[metricType](value),
        timestamp,
        metricType,
        name,
        unit,
        tags
      };

      this._buckets.set(bucketKey, bucketItem);
    } // If value is a string, it's a set metric so calculate the delta from the previous weight.


    const val = typeof value === 'string' ? bucketItem.metric.weight - previousWeight : value;
    (0, _spanUtils.updateMetricSummaryOnActiveSpan)(metricType, name, val, unit, unsanitizedTags, bucketKey); // We need to keep track of the total weight of the buckets so that we can
    // flush them when we exceed the max weight.

    this._bucketsTotalWeight += bucketItem.metric.weight;

    if (this._bucketsTotalWeight >= _constants.MAX_WEIGHT) {
      this.flush();
    }
  }
  /**
   * Flushes the current metrics to the transport via the transport.
   */


  flush() {
    this._forceFlush = true;

    this._flush();
  }
  /**
   * Shuts down metrics aggregator and clears all metrics.
   */


  close() {
    this._forceFlush = true;
    clearInterval(this._interval);

    this._flush();
  }
  /**
   * Flushes the buckets according to the internal state of the aggregator.
   * If it is a force flush, which happens on shutdown, it will flush all buckets.
   * Otherwise, it will only flush buckets that are older than the flush interval,
   * and according to the flush shift.
   *
   * This function mutates `_forceFlush` and `_bucketsTotalWeight` properties.
   */


  _flush() {
    // TODO(@anonrig): Add Atomics for locking to avoid having force flush and regular flush
    // running at the same time.
    // Ref: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Atomics
    // This path eliminates the need for checking for timestamps since we're forcing a flush.
    // Remember to reset the flag, or it will always flush all metrics.
    if (this._forceFlush) {
      this._forceFlush = false;
      this._bucketsTotalWeight = 0;

      this._captureMetrics(this._buckets);

      this._buckets.clear();

      return;
    }

    const cutoffSeconds = Math.floor((0, _time.timestampInSeconds)()) - _constants.DEFAULT_FLUSH_INTERVAL / 1000 - this._flushShift; // TODO(@anonrig): Optimization opportunity.
    // Convert this map to an array and store key in the bucketItem.


    const flushedBuckets = new Map();

    for (const _ref of this._buckets) {
      var _ref2 = _slicedToArray(_ref, 2);

      const key = _ref2[0];
      const bucket = _ref2[1];

      if (bucket.timestamp <= cutoffSeconds) {
        flushedBuckets.set(key, bucket);
        this._bucketsTotalWeight -= bucket.metric.weight;
      }
    }

    for (const _ref3 of flushedBuckets) {
      var _ref4 = _slicedToArray(_ref3, 1);

      const key = _ref4[0];

      this._buckets.delete(key);
    }

    this._captureMetrics(flushedBuckets);
  }
  /**
   * Only captures a subset of the buckets passed to this function.
   * @param flushedBuckets
   */


  _captureMetrics(flushedBuckets) {
    if (flushedBuckets.size > 0) {
      // TODO(@anonrig): Optimization opportunity.
      // This copy operation can be avoided if we store the key in the bucketItem.
      const buckets = Array.from(flushedBuckets).map(([, bucketItem]) => bucketItem);
      (0, _envelope.captureAggregateMetrics)(this._client, buckets);
    }
  }

}

exports.MetricsAggregator = MetricsAggregator;